import { memo, useState } from 'react';
import {
    Box,
    Button,
    Collapse,
    Grid,
    IconButton,
    MenuItem,
    OutlinedInput,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Add, Delete, ExpandMore, ExpandLess } from '@mui/icons-material';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import {
    ConditionDto,
    DynamicSectionDto,
    FormFieldDto,
    FormFieldType,
    FormItemDto,
    FormItemKind,
    LogicOperator,
    RepeatType,
    VisibilityMode,
} from '../document-generation.dto';
import { useAppDispatch } from 'src/redux-app-hooks';
import { removeItemFromTemplate, updateSectionOnTemplate } from '../document-generation-template.slice';
import { generateSimpleID } from 'src/common/generateSimpleID';
import { SortableSectionField } from './SectionField';
import { SortableDocumentTemplateItem } from './DocumentTemplateItem';

interface DocumentTemplateSectionProps {
    index: number;
    section: DynamicSectionDto;
    /** When defined, changes are propagated via callback instead of Redux (nested mode). */
    onUpdate?: (updated: DynamicSectionDto) => void;
    /** When defined, removal is propagated via callback instead of Redux (nested mode). */
    onRemove?: (section: DynamicSectionDto) => void;
    /** Visual depth for nested sections (controls background shading). */
    depth?: number;
}

const DEPTH_COLORS = ['#e8f4f8', '#d6eaf8', '#c5e1f5', '#b3d7f2'];
const DEPTH_BORDERS = ['#90caf9', '#64b5f6', '#42a5f5', '#2196f3'];

export const DocumentTemplateSection = memo(
    ({ index, section, onUpdate, onRemove, depth = 0 }: DocumentTemplateSectionProps) => {
        const { t } = useTranslation();
        const dispatch = useAppDispatch();
        const [expanded, setExpanded] = useState(false);

        const sensors = useSensors(
            useSensor(PointerSensor),
            useSensor(KeyboardSensor, {
                coordinateGetter: sortableKeyboardCoordinates,
            }),
        );

        const bgColor = DEPTH_COLORS[Math.min(depth, DEPTH_COLORS.length - 1)];
        const borderColor = DEPTH_BORDERS[Math.min(depth, DEPTH_BORDERS.length - 1)];

        const updateSection = (updates: Partial<DynamicSectionDto>) => {
            const updated = { ...section, ...updates };
            if (onUpdate) {
                onUpdate(updated);
            } else {
                dispatch(
                    updateSectionOnTemplate({
                        index,
                        section: updated,
                    }),
                );
            }
        };

        const handleRemove = () => {
            if (onRemove) {
                onRemove(section);
            } else {
                dispatch(removeItemFromTemplate(section));
            }
        };

        const addItemToSection = (item: FormItemDto) => {
            updateSection({
                items: [...section.items, item],
            });
        };

        const handleDragEnd = (event: DragEndEvent) => {
            const { active, over } = event;

            if (over && active.id !== over.id) {
                const oldIndex = section.items.findIndex((item) => (item._id ?? item.key) === active.id);
                const newIndex = section.items.findIndex((item) => (item._id ?? item.key) === over.id);
                updateSection({
                    items: arrayMove(section.items, oldIndex, newIndex),
                });
            }
        };

        const addEmptyField = () => {
            const emptyField: FormFieldDto = {
                kind: FormItemKind.FIELD,
                _id: generateSimpleID(),
                key: generateSimpleID(),
                label: '',
                type: FormFieldType.TEXT,
                config: {
                    required: true,
                    options: [],
                    defaultValue: null,
                    placeholder: '',
                    dynamicOptionsKey: null,
                    validation: {
                        minLength: null,
                        maxLength: null,
                        minValue: null,
                        maxValue: null,
                        pattern: null,
                    },
                },
            };
            addItemToSection(emptyField);
        };

        const addEmptySubSection = () => {
            const emptySubSection: DynamicSectionDto = {
                kind: FormItemKind.SECTION,
                _id: generateSimpleID(),
                key: generateSimpleID(),
                label: '',
                items: [],
                repeat: {
                    type: RepeatType.NON_REPEATING,
                },
                visibleWhen: null,
            };
            addItemToSection(emptySubSection);
        };

        return (
            <Grid
                size={12}
                spacing={2}
                container
                direction="row"
                sx={{
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: bgColor,
                    padding: 2,
                    borderRadius: 1,
                    border: `2px solid ${borderColor}`,
                }}
            >
                <Grid size={11}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h6" color="primary">
                            {section.key || `${t('Section')} #${index + 1}`}
                        </Typography>
                        {depth > 0 && (
                            <Typography variant="caption" sx={{ color: 'text.secondary', ml: 0.5 }}>
                                ({t('nested')})
                            </Typography>
                        )}
                        <IconButton size="small" onClick={() => setExpanded(!expanded)}>
                            {expanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                    </Box>
                </Grid>
                <Grid size={1}>
                    <IconButton aria-label={t('Remove section')} onClick={handleRemove} color="error">
                        <Delete />
                    </IconButton>
                </Grid>

                <Collapse in={expanded} timeout="auto" unmountOnExit sx={{ width: '100%' }}>
                    <Grid size={12} container spacing={2}>
                        <Grid size={6}>
                            <TextField
                                required
                                fullWidth
                                label={t('Key')}
                                type="text"
                                variant="standard"
                                size="small"
                                placeholder={t('Section Key')}
                                value={section.key}
                                onChange={(e) => updateSection({ key: e.target.value })}
                            />
                        </Grid>

                        <Grid size={6}>
                            <TextField
                                required
                                fullWidth
                                label={t('Label')}
                                type="text"
                                variant="standard"
                                size="small"
                                placeholder={t('Section Label')}
                                value={section.label}
                                onChange={(e) => updateSection({ label: e.target.value })}
                            />
                        </Grid>

                        <Grid size={12} sx={{ mt: 2 }}>
                            <Typography variant="subtitle1">{t('Repeat Configuration')}</Typography>
                        </Grid>

                        <Grid size={12}>
                            <Select
                                fullWidth
                                label={t('Repeat Type')}
                                value={section.repeat.type}
                                variant="standard"
                                onChange={(e) =>
                                    updateSection({
                                        repeat: {
                                            type: e.target.value as RepeatType,
                                            min: e.target.value === RepeatType.REPEATING ? 0 : undefined,
                                            max: e.target.value === RepeatType.REPEATING ? undefined : undefined,
                                            initial: e.target.value === RepeatType.REPEATING ? 1 : undefined,
                                        },
                                    })
                                }
                                input={<OutlinedInput label={t('Repeat Type')} size="small" notched />}
                            >
                                <MenuItem value={RepeatType.NON_REPEATING}>{t('Non Repeating')}</MenuItem>
                                <MenuItem value={RepeatType.REPEATING}>{t('Repeating')}</MenuItem>
                            </Select>
                        </Grid>

                        {section.repeat.type === RepeatType.REPEATING && (
                            <>
                                <Grid size={4}>
                                    <TextField
                                        fullWidth
                                        label={t('Min Items')}
                                        type="number"
                                        variant="standard"
                                        size="small"
                                        placeholder={t('Minimum')}
                                        value={section.repeat.min ?? 0}
                                        onChange={(e) =>
                                            updateSection({
                                                repeat: {
                                                    ...section.repeat,
                                                    min: parseInt(e.target.value, 10) || 0,
                                                },
                                            })
                                        }
                                    />
                                </Grid>

                                <Grid size={4}>
                                    <TextField
                                        fullWidth
                                        label={t('Max Items')}
                                        type="number"
                                        variant="standard"
                                        size="small"
                                        placeholder={t('Maximum (optional)')}
                                        value={section.repeat.max ?? ''}
                                        onChange={(e) =>
                                            updateSection({
                                                repeat: {
                                                    ...section.repeat,
                                                    max: e.target.value ? parseInt(e.target.value, 10) : undefined,
                                                },
                                            })
                                        }
                                    />
                                </Grid>

                                <Grid size={4}>
                                    <TextField
                                        fullWidth
                                        label={t('Initial Count')}
                                        type="number"
                                        variant="standard"
                                        size="small"
                                        placeholder={t('Initial')}
                                        value={section.repeat.initial ?? 1}
                                        onChange={(e) =>
                                            updateSection({
                                                repeat: {
                                                    ...section.repeat,
                                                    initial: parseInt(e.target.value, 10) || 1,
                                                },
                                            })
                                        }
                                    />
                                </Grid>
                            </>
                        )}

                        <Grid size={12} sx={{ mt: 2 }}>
                            <Typography variant="subtitle1">{t('Visibility Configuration')}</Typography>
                        </Grid>

                        <Grid size={12}>
                            <Button
                                variant="text"
                                size="small"
                                onClick={() => {
                                    if (section.visibleWhen) {
                                        updateSection({ visibleWhen: null });
                                    } else {
                                        updateSection({
                                            visibleWhen: {
                                                mode: VisibilityMode.ALL,
                                                conditions: [],
                                            },
                                        });
                                    }
                                }}
                            >
                                {section.visibleWhen ? t('Remove Visibility Rules') : t('Add Visibility Rules')}
                            </Button>
                        </Grid>

                        {section.visibleWhen && (
                            <>
                                <Grid size={12}>
                                    <Select
                                        fullWidth
                                        label={t('Visibility Mode')}
                                        value={section.visibleWhen.mode}
                                        variant="standard"
                                        onChange={(e) =>
                                            updateSection({
                                                visibleWhen: {
                                                    ...section.visibleWhen!,
                                                    mode: e.target.value as VisibilityMode,
                                                },
                                            })
                                        }
                                        input={<OutlinedInput label={t('Visibility Mode')} size="small" notched />}
                                    >
                                        <MenuItem value={VisibilityMode.ALL}>{t('All conditions must match')}</MenuItem>
                                        <MenuItem value={VisibilityMode.ANY}>{t('Any condition must match')}</MenuItem>
                                    </Select>
                                </Grid>

                                <Grid size={12}>
                                    <Box
                                        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                    >
                                        <Typography variant="body2">{t('Conditions')}</Typography>
                                        <Button
                                            variant="text"
                                            size="small"
                                            startIcon={<Add />}
                                            onClick={() => {
                                                const newCondition: ConditionDto = {
                                                    whenPath: '',
                                                    op: LogicOperator.IS_EXACTLY,
                                                    value: '',
                                                };
                                                updateSection({
                                                    visibleWhen: {
                                                        ...section.visibleWhen!,
                                                        conditions: [...section.visibleWhen!.conditions, newCondition],
                                                    },
                                                });
                                            }}
                                        >
                                            {t('Add Condition')}
                                        </Button>
                                    </Box>
                                </Grid>

                                {section.visibleWhen.conditions.map((condition, conditionIndex) => (
                                    <Grid size={12} container spacing={1} key={conditionIndex}>
                                        <Grid size={4}>
                                            <TextField
                                                fullWidth
                                                label={t('Field Path')}
                                                variant="standard"
                                                size="small"
                                                value={condition.whenPath}
                                                onChange={(e) => {
                                                    const updatedConditions = [...section.visibleWhen!.conditions];
                                                    updatedConditions[conditionIndex] = {
                                                        ...condition,
                                                        whenPath: e.target.value,
                                                    };
                                                    updateSection({
                                                        visibleWhen: {
                                                            ...section.visibleWhen!,
                                                            conditions: updatedConditions,
                                                        },
                                                    });
                                                }}
                                            />
                                        </Grid>
                                        <Grid size={3}>
                                            <Select
                                                fullWidth
                                                label={t('Operator')}
                                                value={condition.op}
                                                variant="standard"
                                                onChange={(e) => {
                                                    const updatedConditions = [...section.visibleWhen!.conditions];
                                                    updatedConditions[conditionIndex] = {
                                                        ...condition,
                                                        op: e.target.value as LogicOperator,
                                                    };
                                                    updateSection({
                                                        visibleWhen: {
                                                            ...section.visibleWhen!,
                                                            conditions: updatedConditions,
                                                        },
                                                    });
                                                }}
                                                input={<OutlinedInput label={t('Operator')} size="small" notched />}
                                            >
                                                {Object.values(LogicOperator).map((op) => (
                                                    <MenuItem key={op} value={op}>
                                                        {t(op)}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </Grid>
                                        <Grid size={4}>
                                            <TextField
                                                fullWidth
                                                label={t('Value')}
                                                variant="standard"
                                                size="small"
                                                value={(condition.value as string) ?? ''}
                                                onChange={(e) => {
                                                    const updatedConditions = [...section.visibleWhen!.conditions];
                                                    updatedConditions[conditionIndex] = {
                                                        ...condition,
                                                        value: e.target.value,
                                                    };
                                                    updateSection({
                                                        visibleWhen: {
                                                            ...section.visibleWhen!,
                                                            conditions: updatedConditions,
                                                        },
                                                    });
                                                }}
                                                disabled={
                                                    condition.op === LogicOperator.IS_EMPTY ||
                                                    condition.op === LogicOperator.IS_NOT_EMPTY
                                                }
                                            />
                                        </Grid>
                                        <Grid size={1}>
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    const updatedConditions = section.visibleWhen!.conditions.filter(
                                                        (_, i) => i !== conditionIndex,
                                                    );
                                                    updateSection({
                                                        visibleWhen: {
                                                            ...section.visibleWhen!,
                                                            conditions: updatedConditions,
                                                        },
                                                    });
                                                }}
                                            >
                                                <Delete />
                                            </IconButton>
                                        </Grid>
                                    </Grid>
                                ))}
                            </>
                        )}

                        <Grid size={12} sx={{ mt: 2 }}>
                            <Typography variant="subtitle1">{t('Section Items')}</Typography>
                        </Grid>

                        <Grid size={12} container spacing={2} sx={{ pl: 3 }}>
                            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                                <SortableContext
                                    items={section.items.map((item) => item._id ?? item.key)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    {section.items.map((item, itemIndex) => {
                                        if (item.kind === FormItemKind.FIELD) {
                                            const field = item as FormFieldDto;
                                            return (
                                                <SortableSectionField
                                                    key={field._id ?? field.key}
                                                    field={field}
                                                    fieldIndex={itemIndex}
                                                    onUpdateField={(idx, updatedField) => {
                                                        updateSection({
                                                            items: section.items.map((f, i) =>
                                                                i === idx ? updatedField : f,
                                                            ),
                                                        });
                                                    }}
                                                    onRemoveField={(fieldToRemove) => {
                                                        updateSection({
                                                            items: section.items.filter(
                                                                (f) => f.key !== fieldToRemove.key,
                                                            ),
                                                        });
                                                    }}
                                                    onAddOption={(fieldKey, option) => {
                                                        updateSection({
                                                            items: section.items.map((f) => {
                                                                if (
                                                                    f.kind === FormItemKind.FIELD &&
                                                                    f.key === fieldKey
                                                                ) {
                                                                    const fieldItem = f as FormFieldDto;
                                                                    return {
                                                                        ...fieldItem,
                                                                        config: {
                                                                            ...fieldItem.config,
                                                                            options: [
                                                                                ...(fieldItem.config.options || []),
                                                                                option,
                                                                            ],
                                                                        },
                                                                    };
                                                                }
                                                                return f;
                                                            }),
                                                        });
                                                    }}
                                                    onRemoveOption={(fieldKey, optionKey) => {
                                                        updateSection({
                                                            items: section.items.map((f) => {
                                                                if (
                                                                    f.kind === FormItemKind.FIELD &&
                                                                    f.key === fieldKey
                                                                ) {
                                                                    const fieldItem = f as FormFieldDto;
                                                                    return {
                                                                        ...fieldItem,
                                                                        config: {
                                                                            ...fieldItem.config,
                                                                            options: (
                                                                                fieldItem.config.options || []
                                                                            ).filter((opt) => opt !== optionKey),
                                                                        },
                                                                    };
                                                                }
                                                                return f;
                                                            }),
                                                        });
                                                    }}
                                                />
                                            );
                                        } else if (item.kind === FormItemKind.SECTION) {
                                            const subSection = item as DynamicSectionDto;
                                            return (
                                                <SortableDocumentTemplateItem
                                                    key={subSection._id ?? subSection.key}
                                                    index={itemIndex}
                                                    item={subSection}
                                                    onUpdate={(updated) => {
                                                        updateSection({
                                                            items: section.items.map((s, i) =>
                                                                i === itemIndex ? updated : s,
                                                            ),
                                                        });
                                                    }}
                                                    onRemove={(toRemove) => {
                                                        updateSection({
                                                            items: section.items.filter((s) => s.key !== toRemove.key),
                                                        });
                                                    }}
                                                    depth={depth + 1}
                                                />
                                            );
                                        }
                                        return null;
                                    })}
                                </SortableContext>
                            </DndContext>
                        </Grid>

                        <Grid size={12}>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Button
                                    variant="outlined"
                                    onClick={addEmptyField}
                                    color="secondary"
                                    startIcon={<Add />}
                                    size="small"
                                >
                                    {t('Add Field to Section')}
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={addEmptySubSection}
                                    color="primary"
                                    startIcon={<Add />}
                                    size="small"
                                >
                                    {t('Add Sub-Section')}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Collapse>
            </Grid>
        );
    },
);
